import {
	extractAdditionalLabels,
	extractCoreLabels
} from 'src/apps/dashboard/helpers/ConfigurationProcessingHelpers';
import { API_URL_PATHS } from 'src/common/consts/ApiUrlPaths';
import { apiCall } from 'src/common/services/api/ServerApi';
import { Logger } from 'src/common/services/Logger';
import { ConfigurationLabel, ConfigurationLabelValue } from 'src/common/types/ConfigurationTypes';

import { Label, LabelType, LabelValue } from '../../pages/label-management/types';

import {
	ADDITIONAL_LABELS_OVERRIDE_PATH,
	CORE_LABELS_OVERRIDE_PATH
} from './labelManagementService.constants';
import { ENTITY_TYPE, LabelsOverrideRequest } from './labelManagementService.types';

const { CENTRAL_CONFIG_PATH } = API_URL_PATHS;

const mapConfigValueToLabelValue = (value: string | ConfigurationLabelValue): LabelValue => {
	if (typeof value === 'string') {
		return { value, dashboardVisible: true };
	}

	return {
		value: value.value,
		dashboardVisible: !(value.excludedFromDashboard ?? false)
	};
};

const mapConfigLabelToLabel = (configLabel: ConfigurationLabel, type: LabelType): Label => ({
	key: configLabel.label,
	displayName: configLabel.displayName ?? configLabel.label,
	description: configLabel.description ?? '',
	type,
	values: configLabel.values.map(mapConfigValueToLabelValue).filter((lv) => lv.value)
});

// The values-override endpoint requires all values as full objects — never plain strings.
const mapLabelToOverrideConfigLabel = (label: Label): ConfigurationLabel => ({
	label: label.key,
	displayName: label.displayName,
	...(label.description ? { description: label.description } : {}),
	values: label.values.map(
		(lv): ConfigurationLabelValue => ({
			value: lv.value,
			excludedFromDashboard: lv.dashboardVisible === false
		})
	)
});

const postLabelsOverride = async (
	path: string,
	productId: string,
	tenantId: string,
	labels: ConfigurationLabel[]
): Promise<void> => {
	const body: LabelsOverrideRequest = {
		entityId: tenantId,
		entityType: ENTITY_TYPE,
		newValue: labels,
		productId
	};

	await apiCall('POST', path, { isConfigUrl: true, data: body });
};

export const fetchLabels = async (productId: string): Promise<Label[]> => {
	try {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const config: any = await apiCall('GET', `${CENTRAL_CONFIG_PATH}?productId=${productId}`, {
			isConfigUrl: true
		});

		const platform = config?.PLATFORM ?? {};
		const coreLabels = extractCoreLabels(platform).map((configLabel) =>
			mapConfigLabelToLabel(configLabel, LabelType.CORE)
		);
		const additionalLabels = extractAdditionalLabels(platform).map((configLabel) =>
			mapConfigLabelToLabel(configLabel, LabelType.CUSTOM)
		);

		return [...coreLabels, ...additionalLabels];
	} catch (error) {
		Logger.error('labelManagementService -> fetchLabels failed:', error);
		throw error;
	}
};

export const saveLabel = async (
	productId: string,
	tenantId: string,
	label: Label,
	currentCoreLabels?: Label[],
	currentCustomLabels?: Label[]
): Promise<void> => {
	try {
		if (label.type === LabelType.CORE) {
			const existingConfigLabels = (currentCoreLabels ?? []).map(mapLabelToOverrideConfigLabel);
			const updatedLabel = mapLabelToOverrideConfigLabel(label);
			const existingIndex = existingConfigLabels.findIndex(
				(configLabel) => configLabel.label === label.key
			);
			const updatedCoreLabels =
				existingIndex >= 0
					? existingConfigLabels.map((configLabel, i) =>
							i === existingIndex ? updatedLabel : configLabel
						)
					: [...existingConfigLabels, updatedLabel];

			await postLabelsOverride(CORE_LABELS_OVERRIDE_PATH, productId, tenantId, updatedCoreLabels);
			return;
		}

		const existingConfigLabels = (currentCustomLabels ?? []).map(mapLabelToOverrideConfigLabel);
		const updatedLabel = mapLabelToOverrideConfigLabel(label);
		const existingIndex = existingConfigLabels.findIndex(
			(configLabel) => configLabel.label === label.key
		);
		const updatedCustomLabels =
			existingIndex >= 0
				? existingConfigLabels.map((configLabel, i) =>
						i === existingIndex ? updatedLabel : configLabel
					)
				: [...existingConfigLabels, updatedLabel];

		await postLabelsOverride(
			ADDITIONAL_LABELS_OVERRIDE_PATH,
			productId,
			tenantId,
			updatedCustomLabels
		);
	} catch (error) {
		Logger.error('labelManagementService -> saveLabel failed:', error);
		throw error;
	}
};

export const deleteLabel = async (
	productId: string,
	tenantId: string,
	labelKey: string,
	labelType: LabelType,
	currentCoreLabels?: Label[],
	currentCustomLabels?: Label[]
): Promise<void> => {
	try {
		if (labelType === LabelType.CORE) {
			// Core labels cannot be removed — clear their values instead
			const updatedCoreLabels = (currentCoreLabels ?? []).map((label) =>
				mapLabelToOverrideConfigLabel(label.key === labelKey ? { ...label, values: [] } : label)
			);

			await postLabelsOverride(CORE_LABELS_OVERRIDE_PATH, productId, tenantId, updatedCoreLabels);
			return;
		}

		// Custom labels are fully removed from the array
		const updatedCustomLabels = (currentCustomLabels ?? [])
			.filter((label) => label.key !== labelKey)
			.map(mapLabelToOverrideConfigLabel);

		await postLabelsOverride(
			ADDITIONAL_LABELS_OVERRIDE_PATH,
			productId,
			tenantId,
			updatedCustomLabels
		);
	} catch (error) {
		Logger.error('labelManagementService -> deleteLabel failed:', error);
		throw error;
	}
};
